import { ClusterItem } from './api/build-baseline-api';
import { TargetSavingsBreakdown } from './item-target-savings';

// Discriminator so the row-config hydrators (SmartBaselineBenchmarkRenderer / BaselinePriceRenderer)
// can tell a Built Baseline commit apart from a real flavor id (e.g. 'F3') or the plain MANUAL_ENTRY
// marker -- both of those are short, known strings; this is a JSON blob starting with '{'.
const BUILT_BASELINE_TYPE = 'built_baseline';

export interface BuiltBaselineSelectedItem {
  itemName: string;
  ppvSavingsPerc: number | null;
  price: number | null;
  spend: number | null;
  strategicSourcingSavingsPerc: number | null;
  totalTargetSavingsPerc: number | null;
  trendSavingsPerc: number | null;
}

// Everything needed to regurgitate how a Built Baseline price was derived, persisted as the VALUE of
// the existing negotiation_price_source / baseline_price_source columns (no new columns -- see the
// Kotor discussion this was designed in). Deliberately flat and self-contained: this is read back
// straight from the DB on reload with nothing else to join against.
export interface BuiltBaselineDetails {
  adjustedTargetPrice: number | null;
  avgPrice: number;
  catName: string;
  clusterNumber: number | null;
  finalPrice: number;
  multiplier: number;
  selectedItems: BuiltBaselineSelectedItem[];
  targetSavings: TargetSavingsBreakdown;
  type: typeof BUILT_BASELINE_TYPE;
  unspscValue: string | null;
}

export const buildBuiltBaselineDetails = (args: {
  adjustedTargetPrice: number | null;
  avgPrice: number;
  catName: string;
  clusterNumber: number | null;
  finalPrice: number;
  itemTargetSavings: Map<string, TargetSavingsBreakdown | null>;
  multiplier: number;
  selectedItems: ClusterItem[];
  targetSavings: TargetSavingsBreakdown;
  unspscValue: string | null;
}): BuiltBaselineDetails => ({
  adjustedTargetPrice: args.adjustedTargetPrice,
  avgPrice: args.avgPrice,
  catName: args.catName,
  clusterNumber: args.clusterNumber,
  finalPrice: args.finalPrice,
  multiplier: args.multiplier,
  selectedItems: args.selectedItems.map(item => {
    const savings = args.itemTargetSavings.get(item.item_name_to_use) ?? null;
    return {
      itemName: item.item_name_to_use,
      ppvSavingsPerc: savings?.ppvSavingsPerc ?? null,
      price: item.price,
      spend: item.spend,
      strategicSourcingSavingsPerc: savings?.strategicSourcingSavingsPerc ?? null,
      totalTargetSavingsPerc: savings?.totalTargetSavingsPerc ?? null,
      trendSavingsPerc: savings?.trendSavingsPerc ?? null,
    };
  }),
  targetSavings: args.targetSavings,
  type: BUILT_BASELINE_TYPE,
  unspscValue: args.unspscValue,
});

export const serializeBuiltBaselineDetails = (details: BuiltBaselineDetails): string => JSON.stringify(details);

/**
 * Safe parse: returns null for a real flavor id, 'manual', malformed JSON, or valid JSON that isn't
 * actually this shape. Callers fall through to their existing flavor-id / manual-entry handling --
 * this must never throw, since it runs on every stored source value, not just Built Baseline ones.
 */
export const parseBuiltBaselineDetails = (source: string | null | undefined): BuiltBaselineDetails | null => {
  if (!source) return null;
  try {
    const parsed = JSON.parse(source) as Partial<BuiltBaselineDetails> | null;
    return parsed?.type === BUILT_BASELINE_TYPE ? (parsed as BuiltBaselineDetails) : null;
  } catch {
    return null;
  }
};
